// The calls strip under the top bar in Blueprint (calls round, step 2; DESIGN.md §9): which calls it
// shows and how each is worded, kept out of the component so it's tested without a browser. With no
// browser phone a call rings people's phones, so the strip follows it: ringing (the full interrupt
// colour, for everyone who takes calls), then answered on a phone while it runs, for those who saw it
// ring or opened it. Not now takes a call off this person's strip; it ends, and it leaves by itself.
import { phoneText, type Call } from '../types.ts';
import { lineWord } from './source-model.ts';
import { stillRinging } from './ringing-model.ts';

export interface StripRow {
  call: Call;
  /** ringing: asking for someone now; taken: answered on a phone, running. */
  kind: 'ringing' | 'taken';
}

/**
 * The strip's calls: those still ringing, oldest first (the longest waiting leads), then the ones this
 * person follows that were answered and still run. None taken off with Not now.
 */
export function stripRows(
  calls: readonly Call[],
  o: { now: number; dismissed: ReadonlySet<string>; followed: ReadonlySet<string> },
): StripRow[] {
  const by = (a: Call, b: Call) => Date.parse(a.startedAt) - Date.parse(b.startedAt);
  const live = calls.filter((c) => !o.dismissed.has(c.id));
  return [
    ...live
      .filter((c) => stillRinging(c, o.now))
      .sort(by)
      .map((call) => ({ call, kind: 'ringing' as const })),
    ...live
      .filter((c) => c.status === 'in_progress' && o.followed.has(c.id))
      .sort(by)
      .map((call) => ({ call, kind: 'taken' as const })),
  ];
}

/** "0:08", "12:03": how long since it came in. */
export function sinceWords(startedAt: string, now: number): string {
  const s = Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Who's calling, the row's name: the person, "One of 2 customers", or "Unknown caller". */
export function callerName(c: Pick<Call, 'matches'>): string {
  if (c.matches.length > 1) return `One of ${c.matches.length} customers`;
  const m = c.matches[0];
  if (!m) return 'Unknown caller';
  return m.contactName || m.customerName;
}

/** What we know, beside the name: the customer (when it isn't the name) and the number. */
export function callerWho(c: Pick<Call, 'matches' | 'from'>): string {
  const m = c.matches.length === 1 ? c.matches[0] : undefined;
  const phone = phoneText(c.from);
  return m && m.contactName && m.contactName !== m.customerName ? `${m.customerName}, ${phone}` : phone;
}

/** The line it came in on: the number called, and its tracking label when it has one. */
export const callLine = (c: Pick<Call, 'to' | 'trackingLabel'>) => lineWord(phoneText(c.to), c.trackingLabel);

/** The state over the name: "Ringing"; answered, who did when the field system says, else "on a phone". */
export const stripState = (r: StripRow) =>
  r.kind === 'ringing'
    ? 'Ringing'
    : r.call.answeredBy
      ? `${r.call.answeredBy} answered`
      : 'Answered on a phone';

/** Read out when a call starts ringing: "Ringing: Marco Bell, (604) 555-0123 · Main line". */
export const ringingSaid = (c: Call) => `Ringing: ${callerName(c)}, ${callerWho(c)}, ${callLine(c)}`;
